import { z } from 'zod'
import { get, isEmpty } from 'lodash'
import { isValidBIC, isValidIBAN } from 'ibantools'

import { PartialRecord } from '@/shared/utils/typesUtils'

import { removeWhiteSpace } from '../utils/stringUtils'
import { Root } from './rootModel'
import { Address } from './addressModels'
import { accountNumberTypes, BankAccount } from './bankAcccountModel'
import { countryCodes, Identifier, identifierTypes } from './debtorModels'
import { PaymentDestination } from './companyModel'
import { OnboardingGateway } from './onboardingModels'
import { isValidAccountNumber, isValidSortCode } from '../utils/inputUtils'
import { RequirementStatus } from './requirementModels'

export type KycDocumentType = 'ID' | 'KBIS' | 'STATUS' | 'UBO'
export type KycDocumentStatus = 'PENDING' | 'VALID' | 'INVALID'
export type KycStatus = 'UNNEEDED' | 'EMPTY' | 'PENDING' | 'VALID' | 'INVALID'

export const contractStatuses = ['notSent', 'pending', 'signed', 'completed', 'voided'] as const
export type ContractStatus = (typeof contractStatuses)[number]

export const userBusinessTypes = ['INDIVIDUAL', 'COMPANY'] as const
export type UserBusinessType = (typeof userBusinessTypes)[number]

export type UserAttributes = {
    /** Email of user; used to fetch Aria-API user */
    email: string

    /** remoteUser.informations.fullName alias */
    fullName?: string

    /** remoteUser.informations.givenName alias */
    givenName?: string

    /** remoteUser.informations.familyName alias */
    familyName?: string

    /** remoteUser.company.address alias */
    address: Address

    /** remoteUser.company.SIRET alias */
    siren?: string

    businessIdentifier?: Identifier

    /** KYC contract status */
    contractStatus: ContractStatus

    /** KYC contract identifier (for contract-worker) */
    contractId?: string

    /** Business name */
    businessName?: string

    /* Business type */
    businessType?: UserBusinessType

    /* Wether or not the user can own the invoice */
    canBeInvoiceOwner?: boolean

    /** Bank account details */
    bankAccount?: BankAccount

    paymentDestinations?: PaymentDestination[]

    /** KYC documents details */
    /** @deprecated: use 'validationStatus' instead */
    kyc: {
        status: KycStatus
        needed: KycDocumentType[]
        documents: PartialRecord<KycDocumentType, KycDocument>
    }
    /** @deprecated: use 'validationStatus' instead */
    onboarding: OnboardingGateway | undefined

    /** Case review status */
    validationStatus?: RequirementStatus
}

export type UserCreationAttributes = Omit<UserAttributes, 'kyc' | 'contractId' | 'fullName'>

export type User = Root &
    UserAttributes & {
        /** Owning company identifier */
        companyId: string
    }

export interface UpdateUserPaymentDestination {
    user: User
    paymentDestination: PaymentDestination
}

export interface deprecated_UpdateUserGateway {
    id: User['id']
    givenName: User['givenName']
    familyName: User['familyName']
    businessName: User['businessName']
    businessType: UserBusinessType
    address: User['address']
    email: User['email']
    businessIdentifier: Identifier
    paymentDestinations: PaymentDestination[]
    canBeInvoiceOwner?: boolean
}

export interface PostUser {
    givenName: User['givenName']
    familyName: User['familyName']
    businessName: User['businessName']
    businessType: UserBusinessType
    address: User['address']
    email: string
    businessIdentifier: Identifier
    paymentDestinations?: PaymentDestination[]
    canBeInvoiceOwner: boolean | undefined
}

export interface PostUserGateway {
    givenName: User['givenName']
    familyName: User['familyName']
    businessName: User['businessName']
    businessType: UserBusinessType
    address: User['address'] | undefined
    email: string
    businessIdentifier: Identifier
    contractStatus: ContractStatus
    paymentDestinations?: PaymentDestination[]
    canBeInvoiceOwner?: boolean
}

export interface KycDocument {
    /* Document identifier */
    fileId: string
    /* Document status */
    status: KycDocumentStatus
}

export interface UpdateUserKycAttributes {
    id: User['id']
    documents: File[]
}

export interface PostUserContract {
    id: User['id']
    email: User['email']
}

export interface UseResendContract {
    id: User['id']
    email: User['email']
}

export type DeleteUser = { userId: User['id'] | undefined }

const convertEmptyToUndefined = z
    .string()
    .optional()
    .transform((value) => (isEmpty(value) ? undefined : value))

const AddressSchema = z.object({
    city: z.string().min(1),
    country: z.enum(countryCodes),
    first: z.string().min(1),
    postal: z.string().min(1),
    region: convertEmptyToUndefined,
    second: convertEmptyToUndefined
})

const BusinessIdentifierSchema = z.object({
    value: z.string().min(1).transform(removeWhiteSpace),
    country: z.enum(countryCodes),
    type: z.enum(identifierTypes)
})

export const PaymentDestinationSchema = z.object({
    accountNumber: z.string().min(1).transform(removeWhiteSpace),
    accountNumberType: z.enum(['IBAN', 'ACCOUNT_NUMBER']),
    type: z.enum(['BANK_ACCOUNT', 'BANK_ACCOUNT_SCAN']),
    routingNumber: convertEmptyToUndefined,
    routingNumberType: z
        .enum(['BIC', 'SORT_CODE'])
        .optional()
        .transform((value) => (isEmpty(value) ? undefined : value))
})

export const UpdateUserSchema = z.object({
    id: z.string().min(1),
    email: z.string().min(1),
    givenName: z.string().min(1),
    familyName: z.string().min(1),
    businessName: z.string().min(1),
    businessType: z.enum(['INDIVIDUAL', 'COMPANY']),
    address: AddressSchema,
    businessIdentifier: BusinessIdentifierSchema,
    canBeInvoiceOwner: z.boolean().optional()
})

export type UpdateUser = z.infer<typeof UpdateUserSchema>
export type UpdateUserGateway = UpdateUser

const validateByAccountType = {
    IBAN: {
        accountNumber: isValidIBAN,
        routingNumber: isValidBIC
    },
    ACCOUNT_NUMBER: {
        accountNumber: isValidAccountNumber,
        routingNumber: isValidSortCode
    }
} as const

export const getPaymentDestinationSchema = (options?: { requireRoutingNumber?: boolean }) => {
    const { requireRoutingNumber = false } = options || {}
    return z
        .object({
            type: z.literal('BANK_ACCOUNT'),
            accountNumberType: z.enum(accountNumberTypes),
            accountNumber: z.string().min(1),
            routingNumber: z.string().optional()
        })
        .superRefine((data, ctx) => {
            // Validate account number format
            const accountValidator = get(validateByAccountType, [data.accountNumberType, 'accountNumber'])
            if (accountValidator && !accountValidator(data.accountNumber)) {
                ctx.addIssue({
                    code: 'custom',
                    params: { code: 'invalid_format', type: data.accountNumberType },
                    path: ['accountNumber']
                })
            }

            // For regular bank accounts, require and validate routing number
            if (data.accountNumberType === 'ACCOUNT_NUMBER') {
                if (!data.routingNumber) {
                    ctx.addIssue({
                        code: 'custom',
                        params: { code: 'invalid_type_received_undefined' },
                        path: ['routingNumber']
                    })
                } else {
                    const routingValidator = validateByAccountType.ACCOUNT_NUMBER.routingNumber
                    if (!routingValidator(data.routingNumber)) {
                        ctx.addIssue({
                            code: 'custom',
                            params: { code: 'invalid_sort_code' },
                            path: ['routingNumber']
                        })
                    }
                }
            }

            // For IBAN accounts, validate routing number
            if (data.accountNumberType === 'IBAN') {
                // Case 1: Routing number provided - validate format
                if (data.routingNumber) {
                    const bicValidator = validateByAccountType.IBAN.routingNumber
                    if (!bicValidator(data.routingNumber)) {
                        ctx.addIssue({
                            code: 'custom',
                            params: { code: 'invalid_bic' },
                            path: ['routingNumber']
                        })
                    }
                    return
                }

                // Case 2: No routing number but required
                if (requireRoutingNumber) {
                    ctx.addIssue({
                        code: 'custom',
                        params: { code: 'invalid_type_received_undefined' },
                        path: ['routingNumber']
                    })
                }
            }
        })
}
